import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Captcha } from "@/components/Captcha";
import { Reveal } from "@/components/site/Reveal";
import { useSiteSettings } from "@/context/SettingsContext";
import { api, formatErr } from "@/lib/api";

const EMPTY = { name: "", email: "", subject: "", message: "", website_hp: "" };

export const ContactSection = () => {
  const { settings } = useSiteSettings();
  const [form, setForm] = useState(EMPTY);
  const [captcha, setCaptcha] = useState({ id: "", answer: "" });
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  const email = settings?.contact_email || "admin@imtheplugnow.com";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...form, captcha_id: captcha.id, captcha_answer: captcha.answer });
      toast.success("Message sent! We'll get back to you soon.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
      setNonce((n) => n + 1);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow text-primary">Contact</p>
          <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">Say hello.</h2>
          <p className="mt-4 text-muted-foreground text-base sm:text-lg">Questions before you apply? We're happy to help.</p>
          <a href={`mailto:${email}`} data-testid="contact-email-link" className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 font-bold hover:border-primary transition-colors">
            <span className="h-10 w-10 rounded-xl bg-primary/15 grid place-items-center"><Mail className="h-5 w-5 text-primary" /></span>
            {email}
          </a>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.1}>
          <form onSubmit={submit} className="rounded-3xl border border-border bg-card p-6 sm:p-10 space-y-4" data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input required placeholder="Your name" value={form.name} onChange={set("name")} className="h-12 rounded-xl" data-testid="contact-name-input" />
              <Input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="h-12 rounded-xl" data-testid="contact-email-input" />
            </div>
            <Input placeholder="Subject" value={form.subject} onChange={set("subject")} className="h-12 rounded-xl" data-testid="contact-subject-input" />
            <Textarea required placeholder="How can we help?" rows={5} value={form.message} onChange={set("message")} className="rounded-xl" data-testid="contact-message-input" />
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website_hp} onChange={set("website_hp")} className="hidden" aria-hidden="true" />
            <Captcha value={captcha} onChange={setCaptcha} nonce={nonce} testPrefix="contact-captcha" />
            <button disabled={busy} data-testid="contact-submit-button" className="group inline-flex h-12 items-center gap-2 px-7 rounded-full bg-primary text-primary-foreground font-bold btn-glow hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 group-hover:translate-x-1 transition-transform" />} Send message
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
